import React, {useCallback, useEffect, useState, useMemo} from 'react'
import {Route, Switch, useRouteMatch, useLocation} from 'react-router'
import {useMultiStyleConfig, VStack, Text, Flex} from '@chakra-ui/react'
// PARTIALS
import BannerLiveChat from './partials/banner-live-chat'
import QuickLinks from './partials/quick-links'
import SearchResults from './partials/search-results'
import FaqTitle from './partials/faq-title'
import GoBackButton from './partials/go-back-button'
// PAGES
import CategoriesList from './partials/categories-list'
import ArticlePage from './partials/article-page'
import ArticlesList from './partials/articles-list'
// COMPONENTS
import usePHPBKApi from '../../custom-api/hooks/use-phpkb-api'
import {FormattedMessage} from 'react-intl'
import Breadcrumb from '../../components/breadcrumb'
import Search from '../../components/search'
// HOOKS
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
//UTILS
import debounce from 'lodash/debounce'
import {useGetPageByType} from '../../custom-api/hooks/use-get-page-by-type'
import routingObject from '../../utils/static-routes'
import {remCalc} from '../../theme/_utils'
import CategoriesRouter from './partials/categories-router'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'

export default function FAQ() {
    const SUGGESTIONS_LENGTH = 5
    const styles = useMultiStyleConfig('Faq', {})
    const {pageContainer} = styles || {}
    const {path} = useRouteMatch()
    const location = useLocation()
    const {usePHPBKFreeTextSearch, usePHPKBbyCountryAndLanguage} = usePHPBKApi()
    const [searchQuery, setSearchQuery] = useState('')
    const [noSearchResults, setNoSearchResults] = useState(false)
    const [isFormSubmitted, setIsFormSubmitted] = useState(false)
    const [pageTitle, setPageTitle] = useState('')
    const [goBackText, setGoBackText] = useState('')
    const [goBackToPath, setGoBackToPath] = useState(routingObject.FAQ_PATH)
    const [hasSubcategory, setHasSubcategory] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const {data: contentstackData} = useGetPageByType('faq_page')
    const {searchAndTitleStyles} = styles || {}

    const [selectedCategory, setSelectedCategory] = useState(null)

    const handleCategorySelect = (category) => {
        setSelectedCategory(category)
    }

    const {data: listOfCategories, isLoading: isListOfCategoriesLoading} =
        usePHPKBbyCountryAndLanguage(
            {
                name: 'WSS',
            },
            {
                enabled: true,
            },
        )

    const navigate = useNavigation()
    const {
        data: queryResults,
        refetch: fetchQueryData,
        isLoading: isQuerySearchLoading,
    } = usePHPBKFreeTextSearch({q: searchQuery}, {enabled: false})

    const formatSearchSuggestions = (articleSuggestions) => {
        if (!articleSuggestions) return []
        return articleSuggestions.hits.hits.map((article) => ({
            article_id: article._source.article_id,
            article_category: article._source.category_name,
            article_title: article._source.article_title,
        }))
    }

    const formatedSuggestions = useMemo(() => {
        let articleSuggestions = formatSearchSuggestions(queryResults)
        return {articleSuggestions}
    }, [queryResults])

    const memoizedFetchCall = useCallback(() => {
        fetchQueryData()
    }, [fetchQueryData])

    useEffect(() => {
        if (!queryResults?.hits?.hits) return
        const hasResults = queryResults.hits.hits.length > 0
        setNoSearchResults(!hasResults)
        if (hasResults && isFormSubmitted) {
            navigate(`${path}/search`)
        }
    }, [queryResults, navigate, path, isFormSubmitted])

    useEffect(() => {
        if (!searchQuery || searchQuery.length < 1) return
        memoizedFetchCall()
    }, [searchQuery])

    const submitSearchHandler = (e, inputValue) => {
        e.preventDefault()
        let searchQuery = inputValue?.current?.value
        if (!searchQuery) return
        setSearchQuery(searchQuery)
        setIsFormSubmitted(true)
    }

    const debouncedSearch = debounce((input) => {
        debouncedSearch.cancel()
        setSearchQuery(input)
    }, 300)

    const handleOnInputChangeCustom = (e) => {
        setIsFormSubmitted(false)
        let searchQuery = e?.target?.value
        if (searchQuery.length >= SUGGESTIONS_LENGTH) {
            debouncedSearch(searchQuery)
            setSearchQuery(searchQuery)
        }
    }

    const breadcrumbCategories = [
        {
            id: 'home',
            name: (
                <FormattedMessage
                    id="faq_page.breadcrumbs.return_to_home"
                    defaultMessage="Return to homepage"
                />
            ),
            c_slugUrl: '/',
        },
        {
            id: 'faq',
            name: (
                <FormattedMessage
                    id="faq_page.breadcrumbs.customer_service_and_support"
                    defaultMessage="Customer Service and Support"
                />
            ),
            c_slugUrl: `${path}`,
        },
    ]

    return (
        <VStack {...pageContainer} data-gtm="faq-page">
            {isListOfCategoriesLoading || (isLoading && <LoadingSpinner />)}

            <VStack {...styles.breadAndBannerContainer}>
                <Flex {...styles.breadcrumbContainer}>
                    <Breadcrumb
                        categories={breadcrumbCategories}
                        w="100%"
                        h="100%"
                        alignSelf="flex-start"
                    />
                </Flex>
                <BannerLiveChat />
            </VStack>
            {location.pathname.endsWith(routingObject.FAQ_PATH) || goBackText === '' ? (
                <></>
            ) : (
                <GoBackButton
                    goBackText={goBackText}
                    goBackToPath={goBackToPath}
                    hasSubcategory={hasSubcategory}
                />
            )}
            <VStack w="100%" h="100%" alignItems="flex-start" gap={remCalc(0)}>
                <FaqTitle
                    title={
                        location.pathname.endsWith(routingObject.FAQ_PATH) ? 'default' : pageTitle
                    }
                />
                {!location.pathname.includes('/a/') && (
                    <VStack
                        width="100%"
                        id="js-r-entry-faq-search"
                        alignItems="flex-start"
                        gap={remCalc(24)}
                    >
                        <Text {...searchAndTitleStyles?.subTitleText}>
                            <FormattedMessage
                                id="faq_page.search.contact"
                                defaultMessage="Send us a question"
                            />
                        </Text>
                        <Search
                            searchInputPlaceholder={
                                <FormattedMessage defaultMessage="Search" id="global.search" />
                            }
                            onSubmitCustom={submitSearchHandler}
                            onSearchInputChangeCustom={handleOnInputChangeCustom}
                            variant="faqSearch"
                            customSuggestionData={formatedSuggestions}
                            showSpellingMessage={noSearchResults}
                        />
                    </VStack>
                )}
            </VStack>

            <Switch>
                <Route exact path={`${path}`}>
                    <CategoriesList
                        setGoBackText={setGoBackText}
                        setGoBackToPath={setGoBackToPath}
                        setHasSubcategory={setHasSubcategory}
                        onCategorySelect={handleCategorySelect}
                        listOfCategories={listOfCategories}
                    />
                </Route>
                <Route exact path={`${path}/:category_name/t/:category_id`}>
                    <CategoriesRouter
                        setGoBackText={setGoBackText}
                        setGoBackToPath={setGoBackToPath}
                        selectedCategory={selectedCategory}
                        setPageTitle={setPageTitle}
                        listOfCategories={listOfCategories}
                        setIsLoading={setIsLoading}
                    />
                </Route>
                <Route exact path={`${path}/:parent_category_name/:category_name/t/:category_id`}>
                    <ArticlesList
                        setGoBackText={setGoBackText}
                        setGoBackToPath={setGoBackToPath}
                        setPageTitle={setPageTitle}
                        listOfCategories={listOfCategories}
                        setIsLoading={setIsLoading}
                    />
                </Route>
                <Route exact path={`${path}/search`}>
                    <SearchResults
                        articles={isFormSubmitted ? queryResults?.hits?.hits ?? [] : []}
                        setGoBackText={setGoBackText}
                        setGoBackToPath={setGoBackToPath}
                        setPageTitle={setPageTitle}
                        isLoading={isQuerySearchLoading}
                    />
                </Route>
                <Route exact path={`${path}/:article_title/a/:article_id`}>
                    <ArticlePage
                        setGoBackText={setGoBackText}
                        setGoBackToPath={setGoBackToPath}
                        setPageTitle={setPageTitle}
                        listOfCategories={listOfCategories}
                        setIsLoading={setIsLoading}
                    />
                </Route>
            </Switch>

            {contentstackData && (
                <QuickLinks
                    quickLinksData={contentstackData?.bottom_components[0]?.quick_link}
                    title={contentstackData?.bottom_components[0]?.links_title}
                />
            )}
        </VStack>
    )
}
